import React, { Fragment } from 'react'
import styled from 'styled-components'
import { graphql } from 'react-apollo'
import Cleave from 'cleave.js'
import moment from 'moment'
import BalanceCell from './BalanceCell'
import Button from '../common/Button'
import UpdateBalanceSheet from './queries/UpdateBalanceSheet'

const Container = styled.div`
  display: grid;
  border: 2px solid #eeeeee;
`
const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr 4fr 1fr;
  padding: 10px 8px;
  border-bottom: 2px solid #eeeeee;
`
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  grid-column-start: 1;
  grid-column-end: 1;
  justify-self: start;
  justify-content: flex-start;
`
const EditButton = Button.extend`
  justify-self: start;
  padding: 5px 13px;
  display: flex;
  justify-content: center;
  align-items: center;
`
const InfoContainer = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  grid-column-start: 3;
  grid-column-end: 3;
`

const FileIcon = styled.a`
  display: inline;
  color: #dd6a01;
  font-size: 10pt;
`

const Icon = styled.i`
  margin-right: 5px;
`

const Title = styled.h3`
  margin-top: 20px;
`
const SubTitleContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 9px solid #f8f8f8;
`
const SubTitle = styled.div`
  font-weight: bold;
  padding: 8px;
`
const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  &:nth-of-type(odd) {
    background: #EEEEEE;
  }
  &:last-of-type {
    background: white;
    border-top: 9px solid #f8f8f8;
  }
`
const Footer = styled(BalanceCell)`
  background: #40bac9;
  color: white;
`
const DateInput = styled.input`
  border-radius: 4px;
  border: 1px solid #ccc !important;
  height: 20px;
  padding: 4px 6px !important;
  text-align: center;
  outline: none;
  width: 65%;
  justify-self: center;
`
const Date = styled.div`
  height: 25px;
  padding-top: 5px;
  text-align: center;
  justify-self: center;
`
class BalanceSheet extends React.Component {
  constructor(props) {
    super(props)
    const balanceSheet = {}
    const balanceSheetCats = props.assets.concat(props.liabilities).map(cat => cat.key)
    for (const cat of balanceSheetCats) {
      balanceSheet[cat] = props.balanceSheet[cat]
    }
    balanceSheet.balanceSheetDate = props.balanceSheet.balanceSheetDate
    this.state = {
      isEditable: false,
      dateCleave: null,
      balanceSheet
    }
  }
  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isEditable && this.state.isEditable) {
      this.setupCleave()
    } else if (prevState.isEditable && !this.state.isEditable) {
      this.destroyCleave()
    }
  }
  setupCleave = () => {
    this.setState({
      dateCleave: new Cleave('#balance-sheet-date', {
        date: true,
        datePattern: ['m', 'd', 'Y'],
        onValueChanged: this.onDateChange
      })
    })
  }
  destroyCleave = () => {
    const { dateCleave } = this.state
    if (dateCleave) {
      dateCleave.destroy()
      this.setState({
        dateCleave: null
      })
    }
  }
  toggleEdit = () => {
    this.setState({
      isEditable: !this.state.isEditable
    })
  }
  isSaveDisabled = () => {
    const { balanceSheetDate } = this.state.balanceSheet
    const { isEditable } = this.state
    const bSDate = moment(balanceSheetDate)
    const oldDate = moment('01/01/1992', 'MM/DD/YYYY')
    if (isEditable && !bSDate.isValid() || bSDate.isBefore(oldDate)) {
      return true
    }
    return false
  }
  onSave = async () => {
    const { updateBalanceSheet, refetch } = this.props
    const { balanceSheet } = this.state
    this.toggleEdit()
    try {
      await updateBalanceSheet({
        variables: {
          input: {
            balanceSheetFields: {
              ...balanceSheet
            },
            balanceSheetId: this.props.balanceSheet.rid
          }
        }
      })
      refetch()
    } catch (err) {
      console.log(err)
    }
  }
  onValueChange = (e, category) => {
    this.setState({
      balanceSheet: {
        ...this.state.balanceSheet,
        [category]: parseFloat(e.target.rawValue, 10)
      }
    })
  }
  onDateChange = e => {
    this.setState({
      balanceSheet: {
        ...this.state.balanceSheet,
        balanceSheetDate: moment(e.target.value, 'MM/DD/YYYY', true)
      }
    })
  }
  renderEditButton = () => {
    const { isEditable } = this.state
    return (
      <ButtonContainer>
        <EditButton disabled={this.isSaveDisabled()} onClick={isEditable ? this.onSave : this.toggleEdit}>
          {isEditable ? 'Save' : 'Edit'}
        </EditButton>
      </ButtonContainer>
    )
  }
  renderDate = () => {
    const { balanceSheetDate } = this.state.balanceSheet
    const { isEditable } = this.state
    const formattedDate = moment(balanceSheetDate).format('MM/DD/YYYY')
    if (!isEditable) {
      return (
        <Date>
          {formattedDate}
        </Date>
      )
    } else {
      return (
        <DateInput
          defaultValue={formattedDate}
          placeholder="MM/DD/YYYY"
          id="balance-sheet-date"
        />
      )
    }
  }
  renderRows = () => {
    const { assets, liabilities, resourceType } = this.props
    const { isEditable } = this.state
    let balanceSheet
    if (resourceType === 'Business') {
      balanceSheet = this.state.balanceSheet
    } else if (resourceType === 'Guarantor') {
      balanceSheet = this.props.balanceSheet
    }
    const maxLength = Math.max(assets.length, liabilities.length)
    const zipped = []
    for (let i = 0; i < maxLength; i++) {
      zipped[i] = [assets[i], liabilities[i]]
    }
    return zipped.map((_, i) => {
      return (
        <Row key={`balance-row-${i}`}>
          <BalanceCell
            isEditable={isEditable}
            title={zipped[i][0].title}
            value={balanceSheet[zipped[i][0].key]}
            valueKey={zipped[i][0].key}
            onValueChange={this.onValueChange}
          />
          <BalanceCell
            isEditable={isEditable}
            title={zipped[i][1].title}
            value={balanceSheet[zipped[i][1].key]}
            valueKey={zipped[i][1].key}
            onValueChange={this.onValueChange}
          />
        </Row>
      )
    })
  }
  renderFooter = () => {
    const { footerTitle, assets, liabilities, resourceType } = this.props
    let balanceSheet
    if (resourceType === 'Business') {
      balanceSheet = this.state.balanceSheet
    } else if (resourceType === 'Guarantor') {
      balanceSheet = this.props.balanceSheet
    }
    const reducer = (acc, obj) => {
      if (typeof balanceSheet[obj.key] !== 'undefined') {
        return acc + balanceSheet[obj.key]
      } else {
        return acc
      }
    }
    const totalAssets = assets.reduce(reducer, 0)
    const totalLiabilities = liabilities.reduce(reducer, 0)
    const footerValue = totalAssets - totalLiabilities
    const totalAssetsText = Number.isNaN(totalAssets) ? '0.00' : totalAssets.toLocaleString('en', { minimumFractionDigits: 2 })
    const totalLiabilitiesText = Number.isNaN(totalLiabilities) ? '0.00' : totalLiabilities.toLocaleString('en', { minimumFractionDigits: 2 })
    return (
      <Fragment>
        <Row>
          <BalanceCell bold title="Total Assets" value={totalAssetsText} />
          <BalanceCell bold title="Total Liabilities" value={totalLiabilitiesText} />
        </Row>
        <Row>
          <div />
          <Footer bold title={footerTitle} value={footerValue.toLocaleString('en', { minimumFractionDigits: 2 })} />
        </Row>
      </Fragment>
    )
  }
  renderHeader = () => {
    const { resourceType, balanceSheetUrl } = this.props
    if (resourceType === 'Business') {
      return (
        <Header>
          {this.renderEditButton()}
          <InfoContainer>
            {this.renderDate()}
            {
              balanceSheetUrl !== '' &&
              <FileIcon
                href={balanceSheetUrl}
                target="_blank"
                rel="noreferrer"
                id={`${resourceType}_balance_document`}
              >
                <Icon className="fa fa-file-text-o" />
                View Document
              </FileIcon>
            }
          </InfoContainer>
        </Header>
      )
    }
  }
  render() {
    const { title } = this.props
    return (
      <section>
        <Title>{title}</Title>
        <Container>
          {this.renderHeader()}
          <SubTitleContainer>
            <SubTitle>Assets</SubTitle>
            <SubTitle>Liabilities</SubTitle>
          </SubTitleContainer>
          {this.renderRows()}
          {this.renderFooter()}
        </Container>
      </section>
    )
  }
}

export default graphql(UpdateBalanceSheet, {
  name: 'updateBalanceSheet'
})(BalanceSheet)
